import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import ReactApexChart from "react-apexcharts";
import Header from "../Header";
import Navigation from "../Navigation";
import Loading from "../loading";
import { primaryGreen, secondeGreen } from "../../utils/colors";
import { getQuestion } from "../../api/Question";
import { getAllLessons } from "../../api/lesson";
import QuestionTable from "../tables/QuestionTableStaticPage";
import Notification from "../Notification";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";

const BOOK_NAMES = {
  1: "ریاضی",
  2: "فارسی و نگارش",
  3: "علوم",
  4: "قرآن",
  5: "املا",
  6: "هدیه های آسمانی",
  7: "مطالعات اجتماعی",
};

const QUESTION_TYPE_MAP = {
  multipleChoice: "چند گزینه‌ای",
  blank: "جای خالی",
  descriptive: "تشریحی",
  shortAnswer: "کوتاه پاسخ",
  adjustable: "جور کردنی",
  truefalse: "صحیح/غلط",
};

const QuestionsStatic = () => {
  // ---------- State ها ----------
  const [loading, setLoading] = useState(true);
  const [allQuestions, setAllQuestions] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [adminData, setAdminData] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [status, setStatus] = useState(null);
  const [edit, setEdit] = useState(false);
  const [questionId, setQuestionId] = useState(null);

  // State های انتخاب‌شده
  const [selectedBase, setSelectedBase] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  // State های آماری
  const [gradeStats, setGradeStats] = useState([]);
  const [bookList, setBookList] = useState([]);
  const [lessonList, setLessonList] = useState([]);
  const [bookStats, setBookStats] = useState(null);
  const [lessonStats, setLessonStats] = useState(null);
  const [questionsOfType, setQuestionsOfType] = useState([]);

  // ---------- تابع‌های کمکی ----------
  const countOfChecked = useCallback((questions) => {
    return questions.filter((q) => q.checkersId && q.checkersId.length > 0)
      .length;
  }, []);

  const getQuestions = useCallback(
    (grade, book, lesson) => {
      let filtered = allQuestions;
      if (grade) {
        filtered = filtered.filter((q) => String(q.grade) === String(grade));
      }
      if (book) {
        filtered = filtered.filter((q) => q.book === book);
      }
      if (lesson) {
        filtered = filtered.filter((q) => q.lessonName === lesson);
      }
      return filtered;
    },
    [allQuestions],
  );

  // ---------- تابع بارگذاری داده ----------
  const loadData = useCallback(
    async (showLoader = true) => {
      if (showLoader) setLoading(true);
      try {
        const [questionsRes, lessonsRes, adminRes] = await Promise.all([
          getQuestion(),
          getAllLessons(),
          GetAdmin(),
        ]);

        const questions = questionsRes?.data?.questions ?? [];
        const lessons = lessonsRes?.data?.lessons ?? [];
        const admin = adminRes?.data ?? adminRes;

        setAllQuestions(questions);
        setAllLessons(lessons);
        setAdminData(admin);

        // محاسبه آمار پایه‌ها
        const gradeMap = {};
        const checkedGradeMap = {};
        questions.forEach((q) => {
          const grade = q.grade !== undefined ? String(q.grade) : "نامشخص";
          gradeMap[grade] = (gradeMap[grade] || 0) + 1;
          if (q.checkersId && q.checkersId.length > 0) {
            checkedGradeMap[grade] = (checkedGradeMap[grade] || 0) + 1;
          }
        });

        const gradeData = Object.keys(gradeMap).map((grade) => ({
          name: `پایه ${grade}`,
          count: gradeMap[grade],
          raw: grade,
          checkedCount: checkedGradeMap[grade] || 0,
        }));
        setGradeStats(gradeData);

        // به‌روزرسانی لیست‌ها در صورت وجود انتخاب قبلی
        if (selectedBase) {
          updateBookList(selectedBase);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [selectedBase],
  );

  // ---------- به‌روزرسانی لیست کتاب‌ها ----------
  const updateBookList = useCallback(
    (grade) => {
      const questions = getQuestions(grade, null, null);
      const bookMap = {};
      questions.forEach((q) => {
        const book = q.book || "نامشخص";
        if (book !== "نامشخص" && BOOK_NAMES[book]) {
          if (!bookMap[book]) {
            bookMap[book] = [];
          }
          bookMap[book].push(q);
        }
      });

      const books = Object.keys(bookMap).map((book) => ({
        bookNumber: book,
        name: BOOK_NAMES[book] || book,
        count: bookMap[book].length,
        checkedCount: countOfChecked(bookMap[book]),
      }));
      setBookList(books);
    },
    [getQuestions, countOfChecked],
  );

  // ---------- به‌روزرسانی لیست درس‌ها ----------
  const updateLessonList = useCallback(
    (grade, book) => {
      const questions = getQuestions(grade, book, null);
      const lessons = allLessons.filter(
        (lesson) =>
          String(lesson.book) === String(book) &&
          String(lesson.grade) === String(grade),
      );

      const lessonMap = {};
      questions.forEach((q) => {
        const lessonName = q.lessonName || "نامشخص";
        if (!lessonMap[lessonName]) {
          lessonMap[lessonName] = [];
        }
        lessonMap[lessonName].push(q);
      });

      const lessonWithCount = lessons.map((lesson) => ({
        lessonId: lesson._id,
        name: lesson.title,
        count: lessonMap[lesson.title]?.length || 0,
        checkedCount: countOfChecked(lessonMap[lesson.title] || []),
      }));
      setLessonList(lessonWithCount);
    },
    [allLessons, getQuestions, countOfChecked],
  );

  // ---------- به‌روزرسانی آمار کتاب ----------
  const updateBookStats = useCallback(
    (grade, book) => {
      const questions = getQuestions(grade, book, null);
      const total = questions.length;
      const easy = questions.filter((q) => q.difficulty === "آسان").length;
      const medium = questions.filter((q) => q.difficulty === "متوسط").length;
      const hard = questions.filter((q) => q.difficulty === "دشوار").length;

      const typeMap = {};
      questions.forEach((q) => {
        const type = q.questionType || "سایر";
        typeMap[type] = (typeMap[type] || 0) + 1;
      });
      const typeStats = Object.keys(typeMap).map((type) => ({
        type,
        count: typeMap[type],
      }));

      setBookStats({ total, easy, medium, hard, typeStats });
    },
    [getQuestions],
  );

  // ---------- به‌روزرسانی آمار درس ----------
  const updateLessonStats = useCallback(
    (grade, book, lesson) => {
      const questions = getQuestions(grade, book, lesson);
      const total = questions.length;
      const easy = questions.filter((q) => q.difficulty === "آسان").length;
      const medium = questions.filter((q) => q.difficulty === "متوسط").length;
      const hard = questions.filter((q) => q.difficulty === "دشوار").length;

      const typeMap = {};
      questions.forEach((q) => {
        const type = q.questionType || "سایر";
        typeMap[type] = (typeMap[type] || 0) + 1;
      });
      const typeStats = Object.keys(typeMap).map((type) => ({
        type,
        count: typeMap[type],
      }));

      setLessonStats({ total, easy, medium, hard, typeStats });

      // نمایش سوالات درس در جدول
      let filtered = questions;
      if (selectedType) {
        filtered = questions.filter((q) => q.questionType === selectedType);
      }
      setQuestionsOfType(filtered);
    },
    [getQuestions, selectedType],
  );

  // ---------- بارگذاری اولیه ----------
  useEffect(() => {
    loadData(true);
  }, [refresh]);

  // ---------- به‌روزرسانی لیست‌ها پس از تغییر allQuestions ----------
  useEffect(() => {
    if (allQuestions.length === 0) return;

    if (selectedBase) {
      updateBookList(selectedBase);
      if (selectedBook) {
        updateLessonList(selectedBase, selectedBook);
        updateBookStats(selectedBase, selectedBook);
        if (selectedLesson) {
          updateLessonStats(selectedBase, selectedBook, selectedLesson);
        }
      }
    }
  }, [allQuestions, selectedBase, selectedBook, selectedLesson, selectedType]);

  // ---------- توابع انتخاب ----------
  const handleGradeSelect = (gradeRaw) => {
    setSelectedBase(gradeRaw);
    setSelectedBook(null);
    setSelectedLesson(null);
    setSelectedType(null);
    setQuestionsOfType([]);
    setBookStats(null);
    setLessonStats(null);
    updateBookList(gradeRaw);
  };

  const handleBookSelect = (bookNumber) => {
    setSelectedBook(bookNumber);
    setSelectedLesson(null);
    setSelectedType(null);
    setQuestionsOfType([]);
    setLessonStats(null);
    updateLessonList(selectedBase, bookNumber);
    updateBookStats(selectedBase, bookNumber);
  };

  const handleLessonSelect = (lessonName) => {
    setSelectedLesson(lessonName);
    setSelectedType(null);
    updateLessonStats(selectedBase, selectedBook, lessonName);
  };

  const handleTypeSelect = (typeKey) => {
    setSelectedType(typeKey);
    const questions = getQuestions(selectedBase, selectedBook, selectedLesson);
    const filtered = questions.filter((q) => q.questionType === typeKey);
    setQuestionsOfType(filtered);
  };

  const handleBackToBook = () => {
    setSelectedLesson(null);
    setSelectedType(null);
    setQuestionsOfType([]);
    setLessonStats(null);
    if (selectedBase && selectedBook) {
      updateBookStats(selectedBase, selectedBook);
    }
  };

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  const refreshDataWithoutScroll = () => {
    loadData(false);
  };

  // ---------- نمودار ----------
  const chartOptions = {
    chart: {
      height: 350,
      type: "bar",
      fontFamily: "IranSansRegular",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const raw = gradeStats[config.dataPointIndex]?.raw;
          if (raw) handleGradeSelect(raw);
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontFamily: "IranSansRegular", colors: ["#333"] },
      formatter: (val) => val,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: gradeStats.map((item) => item.name),
      labels: {
        style: { fontFamily: "IranSans", fontSize: "12px" },
      },
    },
    yaxis: {
      title: {
        text: "تعداد سوالات",
        style: { fontFamily: "IranSans", fontSize: "12px" },
      },
      labels: {
        style: { fontFamily: "IranSans", fontSize: "12px" },
      },
    },
    fill: {
      opacity: 1,
      colors: [secondeGreen],
    },
    tooltip: {
      style: { fontFamily: "IranSansRegular" },
      y: { formatter: (val) => `${val} سوال` },
    },
    grid: {
      row: {
        colors: ["#f3f3f3", "transparent"],
        opacity: 0.5,
      },
    },
  };

  const chartSeries = [
    {
      name: "تعداد سوالات",
      data: gradeStats.map((item) => item.count),
      color: secondeGreen,
    },
  ];

  // ---------- نمایش ----------
  if (loading) return <Loading />;
  if (adminData?.role !== "manager") return <NotFoundPage />;

  return (
    <div className="w-full flex justify-center flex-col items-center">
      {showNotification && (
        <Notification
          {...{
            message:
              status === 200
                ? edit
                  ? "ویرایش سوال موفقیت‌آمیز بود"
                  : "حذف سوال موفقیت‌آمیز بود"
                : status === 201
                  ? "ساخت سوال موفقیت‌آمیز بود"
                  : status === 400
                    ? "خطا : تمام موارد خواسته شده را با دقت تکمیل کنید"
                    : status === 500
                      ? "مشکل سرور لطفاً دقایقی دیگر امتحان کنید"
                      : status === 502
                        ? "مشکل برنامه به سازنده برنامه اطلاع دهید"
                        : status === 401
                          ? "مشکل احراز هویت خروج کنید و دوباره وارد شوید"
                          : status === 403
                            ? "این قابلیت برای شما فعال نیست"
                            : status === 404
                              ? "درس مورد نظر پیدا نشد"
                              : status === 508
                                ? "درس انتخاب نشده است"
                                : "عملیات با موفقیت انجام شد",
            type:
              status === 200 || status === 201
                ? "success"
                : status === 400 ||
                    status === 500 ||
                    status === 502 ||
                    status === 401 ||
                    status === 508
                  ? "error"
                  : "info",
            duration: 3000,
          }}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}
      <Header adminName="مدیر" />
      <div className="flex flex-col md:flex-row gap-6 justify-between w-full">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>
        <div className="w-full md:w-3/4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-800">
              آمار سوالات
            </h2>

            {/* نمودار */}
            <div className="mb-8 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                تعداد سوالات هر پایه (برای انتخاب روی ستون کلیک کنید)
              </h3>
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={350}
              />
            </div>

            {/* انتخاب پایه */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">
                انتخاب پایه
              </h3>
              <div className="flex flex-wrap gap-3">
                {gradeStats.map((item) => (
                  <button
                    key={item.raw}
                    onClick={() => handleGradeSelect(item.raw)}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                      selectedBase === item.raw
                        ? "bg-[#075B4A] text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {item.name} ({item.count}/{item.checkedCount})
                  </button>
                ))}
              </div>
            </div>

            {/* کتاب‌ها */}
            {selectedBase && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-gray-700">
                    کتاب‌های پایه {selectedBase}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedBase(null);
                      setSelectedBook(null);
                      setSelectedLesson(null);
                      setSelectedType(null);
                      setQuestionsOfType([]);
                      setBookStats(null);
                      setLessonStats(null);
                      setBookList([]);
                      setLessonList([]);
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    پاک کردن انتخاب
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {bookList.length > 0 ? (
                    bookList.map(
                      ({ bookNumber, name, count, checkedCount }) => (
                        <button
                          key={bookNumber}
                          onClick={() => handleBookSelect(bookNumber)}
                          className={`px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                            selectedBook === bookNumber
                              ? "bg-[#075B4A] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {name} ({count}/{checkedCount})
                        </button>
                      ),
                    )
                  ) : (
                    <p className="text-gray-500">
                      هیچ کتابی برای این پایه یافت نشد.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* درس‌ها */}
            {selectedBook && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">
                  درس‌های کتاب {BOOK_NAMES[selectedBook] || selectedBook}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {lessonList.length > 0 ? (
                    lessonList.map(
                      ({ lessonId, name, count, checkedCount }) => (
                        <button
                          key={lessonId}
                          onClick={() => handleLessonSelect(name)}
                          className={`px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                            selectedLesson === name
                              ? "bg-[#075B4A] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {name} ({count}/{checkedCount})
                        </button>
                      ),
                    )
                  ) : (
                    <p className="text-gray-500">
                      هیچ درسی برای این کتاب ثبت نشده است.
                    </p>
                  )}
                </div>
                {selectedLesson && (
                  <button
                    onClick={handleBackToBook}
                    className="mt-3 text-sm text-red-500 hover:text-red-700"
                  >
                    بازگشت به آمار کتاب
                  </button>
                )}
              </div>
            )}

            {/* آمار درس */}
            {selectedLesson && lessonStats && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">
                  آمار درس {selectedLesson}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg shadow-md border-r-4 border-green-500">
                    <p className="text-sm text-gray-600">تعداد کل سوالات</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {lessonStats.total}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg shadow-md border-r-4 border-blue-500">
                    <p className="text-sm text-gray-600">سوالات آسان</p>
                    <p className="text-2xl font-bold text-green-600">
                      {lessonStats.easy}
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg shadow-md border-r-4 border-yellow-500">
                    <p className="text-sm text-gray-600">سوالات متوسط</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {lessonStats.medium}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg shadow-md border-r-4 border-red-500">
                    <p className="text-sm text-gray-600">سوالات دشوار</p>
                    <p className="text-2xl font-bold text-red-600">
                      {lessonStats.hard}
                    </p>
                  </div>
                </div>

                {lessonStats.typeStats.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold mb-3 text-gray-700">
                      تفکیک بر اساس نوع سوال
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {lessonStats.typeStats.map((item) => (
                        <button
                          key={item.type}
                          onClick={() => handleTypeSelect(item.type)}
                          className={`p-3 rounded shadow text-center transition-all duration-200 ${
                            selectedType === item.type
                              ? "bg-[#075B4A] text-white"
                              : "bg-white hover:bg-gray-100"
                          }`}
                        >
                          <p className="text-sm text-gray-500">
                            {QUESTION_TYPE_MAP[item.type] || item.type}
                          </p>
                          <p className="text-xl font-bold text-gray-800">
                            {item.count}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* آمار کتاب (زمانی که درسی انتخاب نشده) */}
            {selectedBook && !selectedLesson && bookStats && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">
                  آمار کتاب {BOOK_NAMES[selectedBook] || selectedBook}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg shadow-md border-r-4 border-green-500">
                    <p className="text-sm text-gray-600">تعداد کل سوالات</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {bookStats.total}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg shadow-md border-r-4 border-blue-500">
                    <p className="text-sm text-gray-600">سوالات آسان</p>
                    <p className="text-2xl font-bold text-green-600">
                      {bookStats.easy}
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg shadow-md border-r-4 border-yellow-500">
                    <p className="text-sm text-gray-600">سوالات متوسط</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {bookStats.medium}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg shadow-md border-r-4 border-red-500">
                    <p className="text-sm text-gray-600">سوالات دشوار</p>
                    <p className="text-2xl font-bold text-red-600">
                      {bookStats.hard}
                    </p>
                  </div>
                </div>

                {bookStats.typeStats.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold mb-3 text-gray-700">
                      تفکیک بر اساس نوع سوال
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {bookStats.typeStats.map((item) => (
                        <button
                          key={item.type}
                          onClick={() => handleTypeSelect(item.type)}
                          className={`p-3 rounded shadow text-center transition-all duration-200 ${
                            selectedType === item.type
                              ? "bg-[#075B4A] text-white"
                              : "bg-white hover:bg-gray-100"
                          }`}
                        >
                          <p className="text-sm text-gray-500">
                            {QUESTION_TYPE_MAP[item.type] || item.type}
                          </p>
                          <p className="text-xl font-bold text-gray-800">
                            {item.count}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* جدول سوالات */}
            {questionsOfType.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-gray-700">
                    {selectedLesson
                      ? `سوالات درس ${selectedLesson}`
                      : selectedType
                        ? `سوالات نوع ${QUESTION_TYPE_MAP[selectedType] || selectedType}`
                        : "لیست سوالات"}
                  </h3>
                  <button
                    onClick={() => {
                      setQuestionsOfType([]);
                      if (selectedType) setSelectedType(null);
                      if (selectedLesson) handleBackToBook();
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    بستن لیست
                  </button>
                </div>
                <QuestionTable
                  setAllQuestions={setAllQuestions}
                  allQuestions={allQuestions}
                  setShowNotification={setShowNotification}
                  setStatus={setStatus}
                  setRefresh={handleRefresh}
                  setEdit={setEdit}
                  setQuestionId={setQuestionId}
                  loadData={refreshDataWithoutScroll}
                  questionsOfType={questionsOfType}
                  setQuestionsOfType={setQuestionsOfType}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsStatic;
